<template>
  <b-modal
    ref="myModal"
    size="xlt"
    id="add-model"
    centered
    no-close-on-backdrop
    content-class="config-modal-body"
  >
    <template v-slot:modal-header>
      <b-col md="12">
        <b-row>
          <h2 class="col-12">{{ configurableMaterial.MaterialNumber }}</h2>
        </b-row>
        <transition name="expand">
          <b-row v-if="showDetails" class="justify-content-center">
            <div class="col-12">
              <table class="table">
                <thead>
                  <tr>
                    <th>Item Number</th>
                    <th>Catalog Number</th>
                    <th>Description</th>
                    <th>Quantity</th>
                    <th>List Price</th>
                    <th>Unit Multiplier</th>
                    <th>Net Price</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in configurableMaterial.OrderItems" :key="item.ItemNumber">
                    <td>{{ item.ItemNumber }}</td>
                    <td>{{ item.Material }}</td>
                    <td>{{ item.ShortText }}</td>
                    <td>{{ parseInt(item.Quantity) }}</td>
                    <td><s v-if="specialPrice">{{ formatPrice(item.UnitPrice) }}</s><p v-if="!specialPrice">{{ formatPrice(item.UnitPrice) }}</p></td>
                    <td><s v-if="specialMultiplier">{{ item.Multiplier }}%</s><p v-if="!specialMultiplier">{{ item.Multiplier }}%</p></td>
                    <td>{{ calculatePrice(item, index) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </b-row>
        </transition>
        <transition name="expand">
          <b-row>
            <div class="col" v-if="configurableMaterial.OrderItems">
              <template v-if="!hasErrors && configurableMaterial.OrderItems.length > 0">
                Items ({{ configurableMaterial.OrderItems.length }}):
                <span v-for="(item, index) in configurableMaterial.OrderItems" :key="item.ItemNumber">{{ index === 0 ? '' : ', ' }}{{ item.ShortText }}</span>
              </template>
            </div>
            <div class="col-md-auto" @click="displayErrors" v-if="hasErrors">
              <span v-if="configurableMaterial.Incomplete" class="text-warning">Incomplete! ({{ configurableMaterial.Incomplete }}) </span>
              <span v-if="configurableMaterial.Inconsistent" class="text-danger">Inconsistent! ({{ configurableMaterial.Inconsistent }}) </span>
            </div>
            <div class="col col-lg-2 text-right" v-if="!hasErrors">
              <span v-if="configurableMaterial.OrderItems.length > 0">Price: <strong>{{ formatPrice(configurableMaterial.TotalPrice) }}</strong></span>
              <span v-else>No price.{{ isInquiry ? ' Create inquiry to request the price' : '' }}</span>
              <p class="text-warning" v-for="text in configurableMaterial.WarningMessages" :key="text">{{ text }}</p>
              <p class="text-error" v-for="text in configurableMaterial.ErrorMessages" :key="text">{{ text }}</p>
            </div>
          </b-row>
        </transition>
        <b-button
          v-if="configurableMaterial.OrderItems.length > 0"
          variant="light"
          pill
          size="sm"
          @click="showDetails = !showDetails"
          class=" view-details-btn px-4 py-1 d-flex align-items-center"
        >
            <span>{{ !showDetails ? 'Show Details' : 'Hide Details' }}</span>
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16" height="16"
                fill="currentColor"
                class="bi bi-chevron-down transition-transform"
                :style="{ transform: showDetails ? 'rotate(180deg)' : 'rotate(0)' }"
                viewBox="0 0 16 16"
            >
                <path fill-rule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"/>
            </svg>
        </b-button>
      </b-col>
      <b-modal
        role="dialog"
        v-model="pricingModal"
        ref="myNoteModal"
        :title="(requestPricing ? 'Edit' : 'Add') + ' note for special pricing'"
        size="md"
        centered
        no-close-on-backdrop
        class="dialogModal"
        @ok="addNote"
      >
        <b-form-textarea
          id="textarea"
          v-model="pricingNote"
          placeholder="Add a note..."
          rows="3"
          max-rows="6"
        ></b-form-textarea>
      </b-modal>
    </template>
    <configurable-material ref="refMaterial" v-model="configurableMaterial" :displayErrors="displayErrors" :customerNumber="currentCustomer.customerNo" :divisionCode="currentCustomer.divisionCode"></configurable-material>
    <template v-slot:modal-footer="{}">
      <b-button
        size="sm"
        variant="secondary"
        @click="cancel()"
        class="float-right"
      >
        Cancel
      </b-button>
      <b-button
        size="sm"
        variant="secondary"
        @click="clearAll()"
        class="float-right"
      >
        Clear All
      </b-button>
      <b-button
        size="sm"
        variant="primary"
        @click="saveAndclose()"
        class="float-right"
        :disabled="(configurableMaterial.Incomplete > 0 || configurableMaterial.Inconsistent > 0)"
      >
        Complete
      </b-button>
    </template>
  </b-modal>
</template>

<script lang="ts">
import { Component, Prop, Watch } from 'vue-property-decorator'

import ModelEditor from '@/components/editor/modals/model-editor.vue'
import StatusBadge from '@/components/editor/status-badge.vue'
import EditorEditor from '@/components/editor/utility/editor-editor.vue'
import LoadingText from '@/components/utility/loading-text.vue'
import ConfigurableMaterial from '@/components/configurable-material/configurable-material.vue'
import { ISapConfigureMaterialRequest } from '@/store/configurable-material/types'

import { namespace } from 'vuex-class'

import MxSite from '@/mixins/site'
import OrderProductTabs from './order-product-tabs.vue'
import OrderProductTopology from './order-product-topology.vue'
import { Cart } from '@/store/cart/types'

const nsCart = namespace('cart')
const nsConfigurableMaterial = namespace('configurable-material')
const nsOrder = namespace('orders')
const nsInquiry = namespace('inquiries')

@Component({
  components: {
    'loading-text': LoadingText,
    'editor-editor': EditorEditor,
    'status-badge': StatusBadge,
    'model-editor': ModelEditor,
    'order-product-topology': OrderProductTopology,
    'order-product-tabs': OrderProductTabs,
    'configurable-material': ConfigurableMaterial,
  },
})
export default class extends MxSite {
  @Prop()
  private cartItem!: any

  @Prop()
  private materialNumber!: string

  @Prop()
  private userValues!: any

  @Prop()
  private findingModel!: boolean

  @nsCart.Action('saveCartItem')
  private saveCartItem: any

  @nsCart.Action('deleteCartItem')
  private deleteCartItem: any

  @nsCart.Action('updateCartItem')
  private updateCartItem: any

  @nsCart.Getter('cart')
  public cart!: Cart

  @nsConfigurableMaterial.Action('resetRequest')
  private resetRequest: any

  @nsConfigurableMaterial.Action('getConfiguration')
  private getConfiguration: any

  @nsConfigurableMaterial.Getter('request')
  public request!: ISapConfigureMaterialRequest

  @nsInquiry.Getter('currentCustomer')
  public currentInquiryCustomer!: Cart

  @nsOrder.Getter('currentCustomer')
  public currentOrderCustomer!: Cart

  public showDetails = false
  public specialPrice = false
  public specialMultiplier = false
  public requestPricing = false
  public pricingModal = false
  public pricingNote = ''
  public previousVisibleCharacteristics = []

  public get isInquiry() {
    return this.$route.name !== 'new-order'
  }

  public get currentCustomer() {
    if (this.$route.name === 'new-order') {
      return this.currentOrderCustomer
    }
    return this.currentInquiryCustomer
  }

  public configurableMaterial: any = {
    MaterialNumber: '',
    TotalPrice: 0,
    Inconsistent: 0,
    Incomplete: 0,
    OrderItems: [],
    SpecialPrices: [],
    UserValues: [],
    RequestPricing: false,
    RequestNote: '',
    WarningMessages: [],
    ErrorMessages: [],
  }

  public mounted() {
    const myModal: any = this.$refs.myModal
    myModal.show()
    this.setProperties()
  }

  public blankConfigurableMaterial() {
    return {
      MaterialNumber: '',
      TotalPrice: 0,
      Inconsistent: 0,
      Incomplete: 0,
      OrderItems: [],
      SpecialPrices: [],
      UserValues: [],
      RequestPricing: false,
      RequestNote: '',
      WarningMessages: [],
      ErrorMessages: [],
    }
  }

  private setProperties() {
    this.showDetails = false
    this.configurableMaterial.MaterialNumber = this.materialNumber
    if (this.userValues && this.userValues.length > 0) {
      this.configurableMaterial.UserValues = this.userValues
    }
  }

  @Watch('specialPrice')
  @Watch('specialMultiplier')
  public specialChanged() {
    if (this.specialPrice || this.specialMultiplier) {
      if (this.configurableMaterial.SpecialPrices.length === 0) {
        this.configurableMaterial.SpecialPrices = [...this.configurableMaterial.OrderItems.map(s => { return { 'ItemNumber': s.ItemNumber, 'SpecialPrice': s.UnitPrice, 'SpecialMultiplier': s.Multiplier } })]
      } else {
        if (!this.specialPrice || !this.specialMultiplier) {
          for (let index = 0; index < this.configurableMaterial.OrderItems.length; index++) {
            const element = this.configurableMaterial.OrderItems[index]
            if (!this.specialPrice) {
              this.configurableMaterial.SpecialPrices[index].SpecialPrice = element.SpecialPrice
            }
            if (!this.specialMultiplier) {
              this.configurableMaterial.SpecialPrices[index].SpecialMultiplier = element.SpecialMultiplier
            }
          }
        }
      }
    } else {
      this.configurableMaterial.SpecialPrices = []
    }
  }

  public show() {
    const myModal: any = this.$refs.myModal
    myModal.show()
    this.setProperties()
  }

  public displayErrors() {
    const child: any = this.$refs.refMaterial
    child.displayErrors()
  }

  public formatPrice(val: string | number): string {
    const num = typeof val === 'string' ? parseFloat(val) : val
    return isNaN(num) ? '$0.00' : '$' + num.toFixed(2)
  }

  public clearAll() {
    this.showDetails = false
    const child: any = this.$refs.refMaterial
    child.clearAll()
  }

  public cancel() {
    const myModal: any = this.$refs.myModal
    myModal.hide()
    console.log('cancel', this.cartItem)
    const item = this.cart.items.filter(s => s.id === this.cartItem.id)[0]
    if (item && !item.configured) {
      this.deleteCartItem(item)
    }
  }

  public saveAndclose() {
    const item = this.cart.items.filter(s => s.id === this.cartItem.id)[0]
    if (item) {
      item.totalPrice = this.configurableMaterial.TotalPrice
      item.price = this.configurableMaterial.TotalPrice
      item.characteristics = this.configurableMaterial.UserValues
      item.specialPrices = this.configurableMaterial.SpecialPrices
      item.description = this.orderItemTexts
      item.priceLocked = false
      item.subItems = this.configurableMaterial.OrderItems.map(a => {
        const subItem = {
          MaterialNumber: a.Material,
          Description: a.ShortText,
          Quantity: parseInt(a.Quantity),
          Price: a.UnitPrice,
          TotalPrice: parseFloat(a.NetValue1),
          QuantityUnit: a.QuantityUnit,
          ItemNumber: a.ItemNumber,
          Multiplier: a.Multiplier,
        }
        return subItem
      })
      item.configured = true
    }
    this.updateCartItem({ cartItem: item })
    const myModal: any = this.$refs.myModal
    myModal.hide()
    this.resetRequest()
  }

  public get orderItemTexts() {
    return this.configurableMaterial.OrderItems[0].ShortText // .map(s => s.ShortText).join(', ')
  }

  public get hasErrors() {
    return this.configurableMaterial.Incomplete || this.configurableMaterial.Inconsistent
  }

  public calculatePrice(item: any, index: number) {
    let price = item.NetValue1
    if ((this.specialMultiplier || this.specialPrice) && this.configurableMaterial.SpecialPrices.length > 0) {
      let multiplier = item.Multiplier
      price = item.UnitPrice
      if (this.specialMultiplier) {
        multiplier = this.configurableMaterial.SpecialPrices[index].SpecialMultiplier ?? 0
      }
      if (this.specialPrice) {
        price = this.configurableMaterial.SpecialPrices[index].SpecialPrice ?? 0
      }
      if (multiplier < 0) {
        multiplier = 100 + multiplier * 1
      }
      price = (price * multiplier / 100)
    }
    return this.formatPrice(price)
  }

  public requestPricingClikced() {
    if (this.requestPricing) {
      this.configurableMaterial.RequestNote = ''
      this.requestPricing = false
    } else {
      this.pricingModal = true
    }
  }

  public addNote() {
    this.requestPricing = true
    this.configurableMaterial.RequestNote = this.pricingNote
    this.pricingModal = false
    this.pricingNote = ''
  }

  public editNote() {
    this.pricingNote = this.configurableMaterial.RequestNote
    this.pricingModal = true
  }
}
</script>
<style>
.find-modal-body {
  min-height: 300px;
}
.filter-dropdown {
  min-width: 600px !important;
  /* height: 200px!important; */
  /* overflow: auto; */
}
.filter-tab {
  overflow: auto;
  height: 350px !important;
}
.modal-dialog {
  max-width: 90% !important;
}

.input-quantity {
  width: 30px !important;
  text-align: center;
}

.mobile-tabs-content {
  height: 50vh;
  overflow: auto;
}

@media (max-width: 767px) {
  .category-browser {
    display: none;
  }
  .filter-dropdown {
    min-width: 100vw !important;
  }
  .mobile-field-dropdown {
    text-wrap: balance !important;
    line-height: initial !important;
    min-width: 40vw !important;
  }
}

.modal-dialog,
.config-modal-body {
    /* 80% of window height */
    height: 95%;
}

.modal-body {
    /* 100% = dialog height, 120px = header + footer */
    max-height: calc(100% - 120px);
    overflow-y: scroll;
}

.expand-enter-active, .expand-leave-active {
    transition: all 0.4s ease;
    max-height: 500px;
    overflow: hidden;
}
.expand-enter, .expand-leave-to {
    max-height: 0;
    opacity: 0;
}

.view-details-btn {
    position: absolute;
    bottom: -14px;
    left: 50%;
    transform: translate(-50%, 50%);
    z-index: 10;
}

.badge-custom {
    font-size: 0.75rem;
    padding: 0.4em 0.8em;
}

.dialogModal {
  width: 10px;
}

.clickable {
  cursor:pointer;
}
</style>
